import { useState } from 'react';
import { Loader2, Play, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function StatusPill({ status }) {
  if (status === 'ok') {
    return (
      <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1">
        <CheckCircle2 className="h-3 w-3" />
        ok
      </Badge>
    );
  }
  if (status === 'degraded') {
    return (
      <Badge variant="outline" className="border-amber-500 text-amber-600 dark:text-amber-400 gap-1">
        <AlertTriangle className="h-3 w-3" />
        degraded
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1">
      <XCircle className="h-3 w-3" />
      broken
    </Badge>
  );
}

function ActionBanner({ text }) {
  if (!text) return null;
  return (
    <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded px-2 py-1 mt-1">
      {text}
    </div>
  );
}

function GmailAccountRow({ account }) {
  return (
    <div className="border rounded-lg p-3 space-y-1.5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium font-mono">{account.account_id}</span>
          {account.email && (
            <span className="text-xs text-muted-foreground">{account.email}</span>
          )}
        </div>
        <StatusPill status={account.status} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-muted-foreground">
        <div>
          <span className="font-medium">Refresh token:</span>{' '}
          {account.has_refresh_token ? 'present' : 'missing'}
        </div>
        <div>
          <span className="font-medium">Access token:</span>{' '}
          {account.access_token_status}
        </div>
        <div>
          <span className="font-medium">Token expiry:</span>{' '}
          {account.token_expiry
            ? new Date(account.token_expiry).toLocaleString()
            : 'n/a'}
        </div>
        <div>
          <span className="font-medium">Last scan:</span>{' '}
          {account.last_successful_scan
            ? new Date(account.last_successful_scan).toLocaleString()
            : 'not tracked'}
        </div>
      </div>
      {account.last_error && (
        <div className="text-xs text-muted-foreground bg-muted/30 rounded px-2 py-1 font-mono truncate">
          {account.last_error}
        </div>
      )}
      {account.action_required && <ActionBanner text={account.action_text} />}
    </div>
  );
}

function ChiefOfStaffSection({ data }) {
  return (
    <div className="border rounded-lg p-3 space-y-1.5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm font-medium">Chief of Staff Receipt Sync</span>
        <StatusPill status={data.status} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-muted-foreground">
        <div>
          <span className="font-medium">URL:</span>{' '}
          <span className="font-mono">{data.target_url}</span>
        </div>
        <div>
          <span className="font-medium">Auth:</span> {data.auth_mechanism}
        </div>
        <div>
          <span className="font-medium">Last attempt:</span>{' '}
          <span className="font-mono">{data.last_sync_status}</span>
        </div>
        <div>
          <span className="font-medium">Last success:</span>{' '}
          {data.last_successful_sync
            ? new Date(data.last_successful_sync).toLocaleString()
            : 'never / not tracked'}
        </div>
      </div>
      {data.last_error && (
        <div className="text-xs text-muted-foreground bg-muted/30 rounded px-2 py-1 font-mono truncate">
          {data.last_error}
        </div>
      )}
      {data.action_required && <ActionBanner text={data.action_text} />}
    </div>
  );
}

function FreshnessTable({ rows }) {
  return (
    <div className="overflow-x-auto border rounded-lg">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="text-left px-3 py-1.5 font-medium">Source</th>
            <th className="text-left px-3 py-1.5 font-medium">Last Success</th>
            <th className="text-left px-3 py-1.5 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.source} className="border-b last:border-0">
              <td className="px-3 py-1.5 font-medium">{row.source}</td>
              <td className="px-3 py-1.5 tabular-nums">{row.last_success_relative}</td>
              <td className="px-3 py-1.5">
                <StatusPill status={row.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function IntakeHealthPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.intakeHealth();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Sort action_required rows to the top
  const sortedGmail = result?.gmail_accounts
    ? [...result.gmail_accounts].sort((a, b) => (b.action_required ? 1 : 0) - (a.action_required ? 1 : 0))
    : [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Intake Health (Finance Agent)</CardTitle>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Check
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Gmail credential status and Chief of Staff receipt sync health.
          Read-only, no credentials are written or rotated.
        </p>
      </CardHeader>
      {result && (
        <CardContent className="space-y-5">
          {/* Section C first: at-a-glance summary */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Freshness Summary</h3>
            <FreshnessTable rows={result.freshness_summary || []} />
          </div>

          {/* Section A: Gmail accounts */}
          <div>
            <h3 className="text-sm font-semibold mb-2">
              Gmail Accounts
              <span className="text-xs font-normal text-muted-foreground ml-2">
                ({sortedGmail.length} configured)
              </span>
            </h3>
            <div className="space-y-2">
              {sortedGmail.map((acct) => (
                <GmailAccountRow key={acct.account_id} account={acct} />
              ))}
            </div>
          </div>

          {/* Section B: Chief of Staff */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Chief of Staff Receipt Sync</h3>
            <ChiefOfStaffSection data={result.chief_of_staff} />
          </div>

          {/* Timestamp */}
          {result.checked_at && (
            <p className="text-[10px] text-muted-foreground text-right">
              Checked at {new Date(result.checked_at).toLocaleString()}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
